import { css } from "@emotion/react";
import { useFlag } from "@hermes/flags";
import { useMessage } from "@hermes/intl";
import { toast } from "@hermes/shared";
import {
  ButtonV2,
  type Field,
  Form,
  type FormFields,
  Modal,
  useErrors,
  useScreenDetector,
} from "@hermes/ui";
import { captureMessage } from "@sentry/react";
import { useState } from "react";

import { useSendGTagEvent } from "#internal/app/analytics";
import { useAppConfig } from "#internal/app/config/context";
import { sortDoctorBookingSpecialties } from "#internal/shared/utils";

import type { AppointmentModalProps } from ".";
import { useAdditionalFields } from "./additional-fields";
import { ModalBodyContainer } from "./container";
import { AppointmentForm } from "./form";
import { ModalHeader } from "./header";
import {
  useAppointmentFormFields,
  useAppointmentInsuranceFields,
  useAppointmentOtpFields,
} from "./hooks/use-fields";
import { useSubmitAppointmentForm } from "./hooks/use-submit-form";
import { AppointmentInfoBlock } from "./info-block";
import { AppointmentLegalsBlock } from "./legals-block";
import { OtpStep, useSendOtp } from "./otp";
import { PatientAgeBlock } from "./patient-age";
import { ModalPrice } from "./price";
import { SlotsBlock } from "./slots-block";

export type AppointmentModalStep = "info" | "slots" | "form" | "otp";

export const AppointmentModalAsyncContent = ({
  selectedBranch,
  startDate,
  endDate,
  doctor,
  branchSlots,
  city,
  cityArea,
  initialSlot,
  initialValues,
  type,
  displayServices,
  displayAllReviews,
  selectedDoctorService,
  request_meta,
  onCompleted,
}: AppointmentModalProps) => {
  const { sendGTagEvent } = useSendGTagEvent();
  const { isMobile } = useScreenDetector();
  const message = useMessage();
  const { insurances, country } = useAppConfig();

  const doctorServicesList = doctor.services.filter(
    (service) => service.clinic_branch === selectedBranch.id,
  );

  const errors = useErrors({
    onUnknownErrorKey(key, errors) {
      errors.forEach((error) => toast.error(error));
      captureMessage("[appointment-modal]: unknown validation attribute", {
        level: "warning",
        tags: { booking: "validate" },
        extra: {
          key,
          errors,
        },
      });
    },
  });
  const flag = useFlag();
  const enableConsent = flag({ id: "appointment.consent" });

  const availableInsurances = insurances.filter((item) =>
    doctorServicesList.some((service) => service.insurances.includes(item.id)),
  );

  const fields = useAppointmentFormFields({
    defaultService: selectedDoctorService?.service.id,
    initialSlot,
    errors,
    enableConsent,
    initialValues: initialValues?.fields,
    phoneMask: country.phone.mask,
  });

  const otpFields = useAppointmentOtpFields(errors);

  const selectedService = fields.service.value
    ? doctorServicesList.find(
        (service) => service.service.id === fields.service.value,
      )
    : undefined;

  const { additionalFields, appointment_fields, extractValue } =
    useAdditionalFields({
      errors,
      clinicBranchId: selectedBranch.id,
      initialValues: initialValues?.additionalFields,
    });
  const insuranceFields = useAppointmentInsuranceFields({
    insurances: availableInsurances,
  });

  const [step, setStep] = useState<AppointmentModalStep>(
    selectedService?.on_walk_in_basis ||
      Boolean(selectedService?.price_conditions)
      ? "info"
      : initialSlot
        ? "form"
        : "slots",
  );

  const { sendOtp } = useSendOtp({
    errors,
    onError: () => setStep("form"),
  });
  const enableOtp = flag({
    id: "b25a834b-b76d-4f54-9a10-29aecad190cd",
    description: "otp-enable",
  });

  const { submitAppointmentForm, isSubmitting, isCreatePending } =
    useSubmitAppointmentForm({
      fields,
      additionalFields,
      insuranceFields,
      otpFields,
      errors,
      selectedService,
      extractAdditionalField: extractValue,
      request_meta,
      step,
      onChangeStep: setStep,
      city,
      onCompleted,
      onSendOtp: enableOtp ? sendOtp : undefined,
    });

  const ageBlock = (
    <PatientAgeBlock
      doctor_services={doctorServicesList}
      css={{
        marginBlockEnd: 24,
      }}
    />
  );

  const title = message({
    id: "appointment-modal.title",
    default: "Запись на прием",
  });

  if (step === "otp") {
    return (
      <OtpStep
        fields={otpFields}
        onSubmit={submitAppointmentForm}
        phone={fields.phone.value}
        onChangeStep={setStep}
        errors={errors}
        isPending={isCreatePending}
      />
    );
  }

  return (
    <Modal.Content
      onBackButtonClick={step === "slots" ? () => setStep("form") : undefined}
      title={title}
      main={
        <ModalBodyContainer step={step}>
          <ModalHeader
            doctor={doctor}
            city={city}
            cityArea={cityArea}
            clinicBranchName={selectedBranch.name}
            clinicBranchAddress={selectedBranch.address}
            selectedService={selectedService}
            displayAllReviews={displayAllReviews}
            availableServices={sortDoctorBookingSpecialties(
              displayServices || [],
            )
              .map((service) => service.service_name)
              .truthy()}
            styles={css({
              marginBlockEnd: 20,
              ...(step === "slots" && {
                padding: isMobile ? "10px" : "20px 30px 10px",
                marginBlockEnd: 6,
              }),
            })}
          />
          {step === "info" && (
            <>
              {ageBlock}
              <AppointmentInfoBlock
                liveQueue={selectedService?.on_walk_in_basis}
                avgWaitingTime={selectedService?.avg_waiting_time}
                conditions={selectedService?.price_conditions}
              />
            </>
          )}
          {step === "form" && (
            <>
              {ageBlock}
              {selectedService && (
                <ModalPrice
                  total={selectedService.total}
                  discount={selectedService.discount}
                  price={selectedService.price}
                  styles={css({ marginBlockEnd: 20 })}
                  service_type={selectedService.service.type}
                  insurances={selectedService.insurances}
                  service_name={selectedService.service.name}
                />
              )}
              <Form
                fields={
                  [fields, additionalFields] as Array<
                    FormFields<{
                      [x: string]: Field<any>;
                    }>
                  >
                }
              >
                <AppointmentForm
                  fields={fields}
                  enableConsent={enableConsent}
                  appointment_fields={appointment_fields}
                  additionalFields={additionalFields}
                  doctor_services={doctorServicesList}
                  branchSlots={branchSlots}
                  selectedService={selectedService}
                  onChangeSlotPress={() => setStep("slots")}
                  insurances={availableInsurances}
                  insuranceFields={insuranceFields}
                />
              </Form>
            </>
          )}
          {step === "slots" && selectedService && (
            <SlotsBlock
              city={city}
              doctor={doctor}
              branchSlots={branchSlots}
              defaultStartDate={startDate}
              defaultEndDate={endDate}
              selectedService={selectedService}
              onSlotChange={(slot) => {
                fields.slot.onChange({
                  ...fields.slot,
                  value: slot,
                });
                setStep("form");
              }}
            />
          )}
        </ModalBodyContainer>
      }
      footer={
        step !== "slots" ? (
          <ModalBodyContainer>
            <div
              css={{
                marginBlockEnd: isMobile ? 30 : 20,
                display: "flex",
                flexDirection: "column",
                gap: 14,
              }}
            >
              {step === "info" && (
                <ButtonV2
                  minWidth={230}
                  width={isMobile ? "100%" : undefined}
                  variant="solid"
                  text={message({
                    id: "8d3ad07a-149b-4e43-887a-e780fd97f584",
                    default: "Продолжить",
                  })}
                  onPress={() => setStep(initialSlot ? "form" : "slots")}
                />
              )}
              {step === "form" && (
                <ButtonV2
                  minWidth={230}
                  width={isMobile ? "100%" : undefined}
                  variant="solid"
                  text={
                    type === "reschedule"
                      ? message({
                          id: "common.reschedule-appointment",
                        })
                      : initialValues
                        ? message({ id: "common.rebook-appointment" })
                        : message({
                            id: "appointment-modal.submit",
                            default: "Записаться",
                          })
                  }
                  progress={isSubmitting}
                  id="bookingBtn"
                  onPress={() => {
                    if (initialValues) {
                      sendGTagEvent({
                        event:
                          type === "reschedule"
                            ? "reschedule_change_time"
                            : "reschedule_after_visit",
                      });
                    }
                    sendGTagEvent({
                      event: "submit-appointment-form",
                    });
                    if (
                      [fields.validate(), additionalFields.validate()].every(
                        (res) => res,
                      )
                    ) {
                      submitAppointmentForm();
                    }
                  }}
                />
              )}
              {step === "form" && <AppointmentLegalsBlock />}
            </div>
          </ModalBodyContainer>
        ) : undefined
      }
    />
  );
};
